import React, { useEffect, useState } from 'react';
import { api } from '../api';
import { useAuth } from '../contexts/AuthContext';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { Line } from 'react-chartjs-2';
// Import chart configuration helpers and defaults
import { createVerticalGradient } from '../chartConfig';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import Box from '@mui/material/Box';
import FunnelChart from './FunnelChart';
import Chip from '@mui/material/Chip';
import PeopleIcon from '@mui/icons-material/People';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ShareIcon from '@mui/icons-material/Share';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import ChartCard from './common/ChartCard';
import StatCard from './common/StatCard';
import MaleIcon from '@mui/icons-material/Male';
import FemaleIcon from '@mui/icons-material/Female';
import TransgenderIcon from '@mui/icons-material/Transgender';
import LoadingBlock from './common/LoadingBlock';
import ErrorBlock from './common/ErrorBlock';
import { useTheme } from '@mui/material/styles';

// Using common/StatCard component instead of local one

export default function Dashboard () {
const theme = useTheme();
const { user } = useAuth();
const [summary, setSummary] = useState(null);
const [signupData, setSignupData] = useState(null);
const [completedData, setCompletedData] = useState(null);
const [loading, setLoading] = useState(true);
const [error, setError] = useState(null);
const [dateRange, setDateRange] = useState(() => {
  const end = dayjs();
  const start = end.subtract(30, 'day');
  return { startDate: start.format('YYYY-MM-DD'), endDate: end.format('YYYY-MM-DD') };
});

// Helper to format dates consistently without timezone adjustment
const formatDateString = (dateStr) => {
  // Parse the date parts directly to avoid timezone conversion
  // Expected format: YYYY-MM-DD
  const parts = dateStr.split('-');
  if (parts.length !== 3) {
    // Fallback if not in expected format
    return new Date(dateStr).toLocaleDateString();
  }
  
  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1; // JavaScript months are 0-indexed
  const day = parseInt(parts[2], 10);
  
  // Format the date
  const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
  return new Intl.DateTimeFormat(undefined, options).format(new Date(year, month, day));
};

// Generate today's date in YYYY-MM-DD format
const getTodayString = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

const fetchSignupData = (start, end) => {
  if (!user) return; // Don't make API calls if not authenticated
  
  setLoading(true);
  api.get('/daily-signups', { params: { startDate: start, endDate: end } })
    .then(res => {
      // Log the received data for debugging
      console.log('Signup data received:', res.data);
      
      // Check which dates are marked as today
      const todayData = res.data.filter(item => item.isToday);
      console.log('Days marked as today:', todayData);
      
      // Set the data with correct formatting
      setSignupData(res.data);
      setLoading(false);
      setError(null);
    })
    .catch(err => {
      console.error('Error fetching daily signups:', err);
      setLoading(false);
      setError('Could not load signup data. Please try again later.');
    });
};

const fetchSummaryData = () => {
  if (!user) return; // Don't make API calls if not authenticated
  
  setLoading(true);
  api.get('/summary')
    .then(res => {
      setSummary(res.data);
      setLoading(false);
      setError(null);
    })
    .catch(err => {
      console.error('Error fetching summary data:', err);
      setLoading(false);
      setError('Could not load summary data. Please try again later.');
    });
};

const fetchCompletedData = (start, end) => {
  if (!user) return; // Don't make API calls if not authenticated
  
  setLoading(true);
  api.get('/daily-completions', { params: { startDate: start, endDate: end } })
    .then(res => {
      setCompletedData(res.data);
      setLoading(false);
      setError(null);
    })
    .catch(err => {
      console.error('Error fetching daily completions:', err);
      setLoading(false);
      setError('Could not load completed profiles data. Please try again later.');
    });
};

useEffect(() => {
  if (user) {
    // Only fetch data if user is authenticated
    fetchSummaryData();
    fetchSignupData(dateRange.startDate, dateRange.endDate);
    fetchCompletedData(dateRange.startDate, dateRange.endDate);
  } else {
    // Reset states when no user
    setSummary(null);
    setSignupData(null);
    setCompletedData(null);
    setError(null);
  }
}, [user]);

const handleStartDateChange = (start) => {
  if (start) setDateRange(prev => ({ ...prev, startDate: start.format('YYYY-MM-DD') }));
};
const handleEndDateChange = (end) => {
  if (end) setDateRange(prev => ({ ...prev, endDate: end.format('YYYY-MM-DD') }));
};

const handleRefresh = () => {
  fetchSignupData(dateRange.startDate, dateRange.endDate);
  fetchCompletedData(dateRange.startDate, dateRange.endDate);
};

// Show loading indicator when not authenticated or still loading
if (!user) {
  return (
    <ErrorBlock message="Please sign in to view the dashboard" />
  );
}

if (loading && !summary && !signupData) {
  return <LoadingBlock height={280} />;
}

if (error && !summary) {
  return (
    <ErrorBlock
      message={error}
      onRetry={() => {
        fetchSummaryData();
        fetchSignupData(dateRange.startDate, dateRange.endDate);
        fetchCompletedData(dateRange.startDate, dateRange.endDate);
      }}
    />
  );
}

// Create today's date string for comparison
const today = getTodayString();
console.log('Today computed in frontend:', today);

// Find today's signups/completions count
const todaySignups = signupData ? (signupData.find(item => item.isToday)?.count || 0) : 0;
const todayCompleted = completedData ? (completedData.find(item => item.isToday)?.count || 0) : 0;

// Align series by date and build two-line chart
const alignSeriesByDate = (signups = [], completions = []) => {
  const all = new Set([...(signups || []).map(r => r.date), ...(completions || []).map(r => r.date)]);
  const labelsSorted = Array.from(all).sort((a, b) => a.localeCompare(b));
  return {
    labels: labelsSorted.map(d => {
      const isToday = (signups || []).find(r => r.date === d)?.isToday || (completions || []).find(r => r.date === d)?.isToday;
      const formatted = formatDateString(d);
      return isToday ? `Today (${formatted})` : formatted;
    }),
    sCounts: labelsSorted.map(d => (signups || []).find(r => r.date === d)?.count || 0),
    cCounts: labelsSorted.map(d => (completions || []).find(r => r.date === d)?.count || 0),
  };
};

const aligned = alignSeriesByDate(signupData || [], completedData || []);
const chartData = {
  labels: aligned.labels,
  datasets: [
    {
      label: 'Daily Sign-ups',
      data: aligned.sCounts,
      borderColor: theme.palette.primary.main,
      backgroundColor: function(context) {
        const { chart } = context; const { ctx, chartArea } = chart;
        if (!chartArea) return 'rgba(149, 62, 241, 0.1)';
        return createVerticalGradient(ctx, chartArea, theme.palette.primary.main, 0.2);
      },
      tension: 0.25,
      fill: true,
      pointRadius: 0
    },
    {
      label: 'Completed profiles',
      data: aligned.cCounts,
      borderColor: '#4CAF50',
      tension: 0.25,
      fill: false,
      pointRadius: 0
    }
  ]
};

// Chart options
const chartOptions = {
  responsive: true,
  layout: { padding: { top: 8, right: 12 } },
  plugins: {
    legend: {
      position: 'top',
    },
    title: {
      display: true,
      text: 'Daily Sign-ups'
    },
    tooltip: {
      callbacks: {
        title: (tooltipItems) => {
          const item = tooltipItems[0];
          return item.label.startsWith('Today') ? 'Today' : item.label;
        }
      }
    }
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: {
        precision: 0 // Only show whole numbers
      }
    },
    x: {
      ticks: {
        callback: function(value) {
          if (typeof this.getLabelForValue === 'function') {
            const label = this.getLabelForValue(value);
            if (window.innerWidth < 600 && typeof label === 'string') {
              return label.replace(/^(\d{2})\/(\d{2})\/(\d{2})$/, '$1/$2');
            }
            return label;
          }
          return value;
        }
      }
    }
  }
};

// Gender helpers
const genderKey = (g = '') => {
  const v = String(g).toLowerCase();
  if (v.startsWith('m')) return 'male';
  if (v.startsWith('f')) return 'female';
  if (v.includes('non')) return 'nonbinary';
  return 'other';
};
const genderMeta = {
  male:      { label: 'Male', color: '#2196f3', icon: <MaleIcon fontSize="small" /> },
  female:    { label: 'Female', color: '#e91e63', icon: <FemaleIcon fontSize="small" /> },
  nonbinary: { label: 'Non-binary', color: '#9c27b0', icon: <TransgenderIcon fontSize="small" /> },
  other:     { label: 'Other', color: '#607d8b', icon: <TransgenderIcon fontSize="small" /> },
};
const toBreakdown = (rows = []) => {
  const order = ['male', 'female', 'nonbinary', 'other'];
  const agg = rows.reduce((acc, r) => {
    const key = genderKey(r.gender);
    const val = r.count ?? r.cnt ?? 0;
    acc[key] = (acc[key] || 0) + val;
    return acc;
  }, {});
  return order
    .filter(k => agg[k] > 0)
    .map(k => ({ label: genderMeta[k].label, value: agg[k], icon: genderMeta[k].icon, color: genderMeta[k].color }));
};

return (
<Grid container spacing={2}>
{summary && (
  <>
    <Grid item xs={12} md={3}><StatCard title="Total users" value={summary.totalUsers} breakdown={toBreakdown(summary.totalUsersByGender)} icon={<PeopleIcon />} /></Grid>
    <Grid item xs={12} md={3}><StatCard title="Completed profiles" value={summary.completed} breakdown={toBreakdown(summary.completedByGender)} icon={<CheckCircleIcon />} /></Grid>
    <Grid item xs={12} md={3}><StatCard title="Total referrals" value={summary.referrals} icon={<ShareIcon />} /></Grid>
    <Grid item xs={12} md={3}><StatCard title="Tokens distributed" value={summary.totalTokens} icon={<MonetizationOnIcon />} /></Grid>
  </>
)}

  <Grid item xs={12}>
    <ChartCard
      title="Sign-ups over time"
      actions={(
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <DatePicker label="Start" value={dayjs(dateRange.startDate)} onChange={handleStartDateChange} slotProps={{ textField: { size: 'small' } }} />
          <DatePicker label="End" value={dayjs(dateRange.endDate)} onChange={handleEndDateChange} slotProps={{ textField: { size: 'small' } }} />
          <Button variant="outlined" onClick={handleRefresh}>Apply</Button>
          {signupData && (<Chip color="primary" variant="outlined" label={`Today sign-ups: ${todaySignups}`} />)}
          {completedData && (
            <Chip color="success" variant="outlined" label={`Today completed: ${todayCompleted}`} />
          )}
        </Box>
      )}
    >
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <CircularProgress />
        </Box>
      ) : signupData && signupData.length > 0 ? (
        <Line data={chartData} options={{ ...chartOptions, maintainAspectRatio: false }} />
      ) : (
        <Typography variant="body2" color="text.secondary" align="center" sx={{ p: 3 }}>
          No sign-up data available for the selected period
        </Typography>
      )}
    </ChartCard>
  </Grid>
  
  {/* Funnel Progression Chart */}
  <Grid item xs={12}>
    <FunnelChart />
  </Grid>
</Grid>
);
} 